/**
 * <medcline-product-form> — PDP variant picker + qty + ATC behavior.
 *
 * - Reads product JSON from inline <script data-product-json>
 * - Reacts to radio changes, computes selected variant from option values
 * - Updates hidden #id field, price display, ATC label, image gallery
 * - Updates URL with ?variant= so refresh keeps state
 * - Form submit is intercepted globally by <medcline-cart-drawer>; this element just keeps
 *   state in sync.
 */
class MedClineProductForm extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-product-json]');
    this.product = dataEl ? JSON.parse(dataEl.textContent) : null;
    if (!this.product) return;

    this.form = this.querySelector('[data-product-form]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.addBtn = this.querySelector('[data-add-button]');
    this.addText = this.querySelector('[data-add-text]');
    this.priceEl = this.querySelector('.product-buybox__price');
    this.skuEl = this.querySelector('[data-variant-sku] span');
    this.qtyInput = this.querySelector('[data-qty-input]');

    this.optionInputs = Array.from(this.querySelectorAll('[data-option-input]'));
    this.optionDisplays = Array.from(this.querySelectorAll('[data-option-display]'));

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.onOptionChange());
    });

    if (this.qtyInput) {
      const up = this.querySelector('[data-qty-up]');
      const down = this.querySelector('[data-qty-down]');
      up && up.addEventListener('click', () => this.qtyInput.stepUp());
      down && down.addEventListener('click', () => {
        if (Number(this.qtyInput.value) > 1) this.qtyInput.stepDown();
      });
    }

    // If URL has ?variant=, sync state to that on load
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) this.syncFromVariantId(Number(urlVariant));
  }

  selectedOptions() {
    const opts = {};
    this.optionInputs
      .filter((i) => i.checked)
      .forEach((i) => {
        const m = i.name.match(/options\[(.+)\]/);
        if (m) opts[m[1]] = i.value;
      });
    return opts;
  }

  matchVariant(selected) {
    return this.product.variants.find((v) => {
      return this.product.options.every((optName, idx) => {
        return v.options[idx] === selected[optName];
      });
    });
  }

  syncFromVariantId(id) {
    const v = this.product.variants.find((x) => x.id === id);
    if (!v) return;
    // Don't auto-pick an unavailable variant from the URL when other
    // variants are buyable — the Liquid render already chose the first
    // available variant, so leaving that selection in place keeps ATC
    // enabled instead of disabling it on landing.
    if (!v.available && this.product.variants.some((x) => x.available)) return;
    // Re-check radios to match the variant's option values
    this.product.options.forEach((optName, idx) => {
      const value = v.options[idx];
      const input = this.optionInputs.find(
        (i) => i.name === `options[${optName}]` && i.value === value
      );
      if (input) input.checked = true;
    });
    this.onOptionChange();
  }

  onOptionChange() {
    const selected = this.selectedOptions();
    const variant = this.matchVariant(selected);

    // Update each option's "selected" display label
    this.optionDisplays.forEach((el, i) => {
      const optName = this.product.options[i];
      if (optName && selected[optName] != null) el.textContent = selected[optName];
    });

    if (!variant) {
      this.setUnavailable();
      return;
    }

    this.variantInput.value = variant.id;
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateSku(variant);
    this.updateGallery(variant);
    this.updateUrl(variant.id);
  }

  setUnavailable() {
    this.addBtn.disabled = true;
    this.addText.textContent = this.addText.dataset.unavailable || 'Unavailable';
  }

  updatePrice(variant) {
    if (!this.priceEl) return;
    const current = this.priceEl.querySelector('[data-price-current]');
    const compare = this.priceEl.querySelector('[data-price-compare]');
    if (current) current.textContent = this.formatMoney(variant.price);
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compare) {
        compare.textContent = this.formatMoney(variant.compare_at_price);
        compare.hidden = false;
      } else {
        const s = document.createElement('s');
        s.className = 'price__compare text-muted';
        s.setAttribute('data-price-compare', '');
        s.textContent = this.formatMoney(variant.compare_at_price);
        current.insertAdjacentElement('afterend', s);
      }
    } else if (compare) {
      compare.remove();
    }
  }

  updateAvailability(variant) {
    if (variant.available) {
      this.addBtn.disabled = false;
      this.addText.textContent = this.addText.dataset.add || this.addText.textContent;
    } else {
      this.addBtn.disabled = true;
      this.addText.textContent = this.addText.dataset.soldOut || 'Sold out';
    }
  }

  updateSku(variant) {
    if (this.skuEl) this.skuEl.textContent = variant.sku || '';
  }

  updateGallery(variant) {
    // Variant changes no longer auto-scroll the gallery — users found the
    // jump disorienting, especially when they had already swiped to a
    // detail shot. The featured-media slide can still be reached via the
    // thumbnail strip or the gallery arrows.
    void variant;
  }

  updateUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.product.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP',
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }
}

if (!customElements.get('medcline-product-form')) {
  customElements.define('medcline-product-form', MedClineProductForm);
}

// Okendo renders asynchronously, so restore incoming review deep links once
// the widget is ready. Stop if the visitor chooses to move elsewhere first.
(() => {
  const target = document.getElementById('reviews');
  const widget = target?.querySelector('[data-oke-widget]');
  if (window.location.hash !== '#reviews' || !widget) return;
  let finished = false;
  const observer = new MutationObserver(showReviews);
  const stop = () => {
    finished = true;
    observer.disconnect();
    window.removeEventListener('load', showReviews);
    for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      window.removeEventListener(event, stop);
    }
  };
  function showReviews() {
    if (finished || document.readyState !== 'complete' || !widget.hasAttribute('data-oke-rendered')) return;
    stop();
    requestAnimationFrame(() => {
      if (window.location.hash === '#reviews') target.scrollIntoView({ block: 'start' });
    });
  }
  observer.observe(widget, { attributes: true, attributeFilter: ['data-oke-rendered'] });
  window.addEventListener('load', showReviews, { once: true });
  for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
    window.addEventListener(event, stop, { once: true, passive: true });
  }
  setTimeout(stop, 15000);
  showReviews();
})();

// Gallery thumb click → smooth-scroll the main gallery to that slide.
// The main gallery is a horizontal scroll-snap container, so we scroll
// the slide into view instead of toggling an .is-active class.
document.addEventListener('click', (e) => {
  const thumb = e.target.closest('[data-gallery-thumb]');
  if (!thumb) return;
  const id = thumb.dataset.mediaId;
  const slide = document.querySelector(`[data-gallery-slide][data-media-id="${id}"]`);
  if (slide) slide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
});

// Sync the active thumb with whichever slide is currently snapped into view.
// IntersectionObserver fires when a slide crosses the 60% visibility threshold,
// keeping the thumbnail strip honest as the user swipes / scrolls the main gallery.
(() => {
  const main = document.querySelector('.product-gallery__main');
  if (!main || !('IntersectionObserver' in window)) return;
  const slides = [...main.querySelectorAll('[data-gallery-slide]')];
  if (!slides.length) return;

  const setActive = (id) => {
    document.querySelectorAll('[data-gallery-thumb]').forEach((t) => {
      const isActive = t.dataset.mediaId === id;
      t.classList.toggle('is-active', isActive);
      if (isActive) {
        // Keep the active thumbnail visible without moving the page away from
        // a deep link such as #reviews when the gallery first initializes.
        const strip = t.closest('.product-gallery__thumbs');
        if (!strip) return;
        const thumbRect = t.getBoundingClientRect();
        const stripRect = strip.getBoundingClientRect();
        const left = thumbRect.left < stripRect.left
          ? thumbRect.left - stripRect.left
          : Math.max(0, thumbRect.right - stripRect.right);
        if (left) strip.scrollBy({ left, behavior: 'smooth' });
      }
    });
  };

  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
        setActive(entry.target.dataset.mediaId);
      }
    }
  }, { root: main, threshold: [0.6] });

  slides.forEach((s) => io.observe(s));
})();
